const DB = 'sv-session'
const STORE = 'kv'
const TOKENS_KEY = 'tokens'
const OPEN_MS = 800

const memory = new Map<string, string>()
let generation = 0
let pending: Promise<void> = Promise.resolve()

function withTimeout<T>(work: Promise<T>, ms = OPEN_MS): Promise<T | null> {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(null), ms)
    work.then(
      (value) => {
        clearTimeout(timer)
        resolve(value)
      },
      () => {
        clearTimeout(timer)
        resolve(null)
      },
    )
  })
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('no-idb'))
      return
    }
    const req = indexedDB.open(DB, 1)
    req.onupgradeneeded = () => {
      const db = req.result
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE)
    }
    req.onsuccess = () => resolve(req.result)
    req.onerror = () => reject(req.error)
  })
}

async function idbGet(key: string): Promise<string | null> {
  if (typeof indexedDB === 'undefined') return memory.get(key) ?? null
  const db = await withTimeout(openDb())
  if (!db) return memory.get(key) ?? null
  const value = await withTimeout(new Promise<string | null>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly')
    const req = tx.objectStore(STORE).get(key)
    req.onsuccess = () => resolve(typeof req.result === 'string' ? req.result : null)
    req.onerror = () => reject(req.error)
  }))
  return value ?? memory.get(key) ?? null
}

async function idbSet(key: string, value: string): Promise<void> {
  memory.set(key, value)
  if (typeof indexedDB === 'undefined') return
  const db = await withTimeout(openDb())
  if (!db) return
  await withTimeout(new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).put(value, key)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  }))
}

async function idbDelete(key: string): Promise<void> {
  memory.delete(key)
  if (typeof indexedDB === 'undefined') return
  const db = await withTimeout(openDb())
  if (!db) return
  await withTimeout(new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite')
    tx.objectStore(STORE).delete(key)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  }))
}

export interface DurableTokens {
  access: string
  refresh: string | null
}

export function writeDurableTokens(access: string, refresh: string | null): void {
  const gen = generation
  const snapshot = JSON.stringify({ access, refresh })
  pending = pending.catch(() => {}).then(async () => {
    if (gen !== generation) return
    await idbSet(TOKENS_KEY, snapshot)
  })
}

export function clearDurableTokens(): void {
  generation += 1
  pending = pending.catch(() => {}).then(async () => {
    await idbDelete(TOKENS_KEY)
  })
}

export function flushDurableTokens(): Promise<void> {
  return pending.catch(() => {})
}

export async function readDurableTokens(): Promise<DurableTokens | null> {
  const gen = generation
  const raw = await idbGet(TOKENS_KEY)
  if (gen !== generation || !raw) return null
  try {
    const parsed = JSON.parse(raw) as { access?: unknown; refresh?: unknown }
    const access = typeof parsed.access === 'string' ? parsed.access : ''
    const refresh = typeof parsed.refresh === 'string' ? parsed.refresh : null
    if (!access && !refresh) return null
    return { access, refresh }
  } catch {
    return null
  }
}

export async function readDurableFlag(key: string): Promise<string | null> {
  return idbGet(key)
}

export function writeDurableFlag(key: string, value: string): Promise<void> {
  return idbSet(key, value).catch(() => {})
}

export function clearDurableFlag(key: string): Promise<void> {
  return idbDelete(key).catch(() => {})
}
